import { DownloadIcon, PlusIcon } from "lucide-react";

import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { currencyFormatter } from "../../utils/currency";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TableFilterRow } from "../TableFilterRow";

type TestSpec = RouterOutputs["admin"]["testSpec"]["list"][0];

export function TestSpecsTable({ tests }: { tests: TestSpec[] | undefined }) {
  const reorder = trpc.admin.testSpec.updateOrder.useMutation({
    onError(err) {
      toast.error("Error reordering results", {
        description: err.message,
      });
    },
  });

  const { ref, options, filterTextProps } = usePersistedAdminGridOptions(
    "test-specs",
    tests,
    [
      {
        field: "name",
        cellRenderer: (params) => (
          <Link
            href={{ to: "/admin/tests/$id/edit", params: { id: params.id } }}
          >
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          name: params.data.name,
          id: params.data.id,
        }),
        minWidth: 280,
        rowDrag: true,
      },
      { field: "code" },
      {
        field: "manufacturer",
        filter: "agSetColumnFilter",
        initialHide: true,
      },
      {
        field: "type",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <Badge size="sm">{params.value}</Badge>,
      },
      { field: "selectable", filter: "agSetColumnFilter" },
      {
        headerName: "In use",
        filter: "agSetColumnFilter",
        cellDataType: "boolean",
        initialHide: true,
        valueGetter: ({ data }) => {
          if (!data) return false;
          return data._count.panels > 0 || data.selectable;
        },
      },
      {
        field: "price",
        type: "rightAligned",
        filter: "agNumberColumnFilter",
        cellRenderer: (params) => (
          <Text isDanger={params.value === 0}>
            {currencyFormatter(params.value)}
          </Text>
        ),
        valueGetter: (params) => params.data?.price?.toNumber() ?? 0,
      },
    ],
    {
      getRowId: (t) => t.data.id.toString(),
      onRowDragEnd: (e) => {
        const newRows: { testSpecId: number; displayOrder: number }[] = [];
        e.api.forEachNode((node, idx) => {
          if (node.data?.id) {
            newRows.push({
              testSpecId: node.data.id,
              displayOrder: idx,
            });
          }
        });
        reorder.mutate(newRows);
      },
    },
  );

  return (
    <>
      <TableFilterRow inputProps={filterTextProps}>
        <div className="flex items-center gap-2">
          <a
            href="/api/export/tests"
            className={buttonVariants({ variant: "outline" })}
            target="_blank"
            rel="noopener"
          >
            <DownloadIcon />
            Export
          </a>

          <Link href={{ to: "/admin/tests/new" }} className={buttonVariants()}>
            <PlusIcon />
            Add <span className="hidden sm:inline">&nbsp;Test Spec</span>
          </Link>
        </div>
      </TableFilterRow>
      <AdminGrid {...options} ref={ref} />
    </>
  );
}
